import { FormattedMessage } from 'react-intl';
import { Dialog, DialogContent, DialogActions, Button, Typography, RadioGroup, FormControlLabel, Radio, List, ListItem } from '@mui/material';

import { IPipaConsent, PipaConsent, PipaConsentValue } from '../classes/PipaConsent';

export interface IPipaConsentDialogState {
    data: IPipaConsent;
}

export interface IPipaConsentDialogProps {
    show?: boolean;
    data: IPipaConsent;
    onChange: (data: IPipaConsent) => void;
    onCancel: (state: IPipaConsentDialogState) => void;
    onConfirm: (state: IPipaConsentDialogState) => void;
}

/**
 * Modal for PIPA consent, required only for Korea.
 * This modal presents three consent sections regarding personal information collection, third-party sharing, and international transfer.
 */
export function PipaConsentDialog(props: IPipaConsentDialogProps): JSX.Element | null {
    const { show = true, onCancel, onConfirm, onChange, data } = props;

    if (!show) return null;

    const handleOnChangePipaConsent = (
        field: keyof PipaConsent,
        value: PipaConsentValue
    ) => {
        const updated = {
            ...props.data,
            [field]: value,
        }

        onChange(updated)
    }

    const handleOnClickPipaConsentDialogCancel = () => {
        onCancel({ data: data });
    }

    const handleOnClickPipaConsentDialogConfirm = () => {
        onConfirm({ data: data });
    }

    const handleClose = () => {
        onCancel({ data: data });
    }

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            aria-labelledby="pipa-consent-dialog-title"
            aria-describedby="pipa-consent-dialog-description"
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    maxHeight: '80vh',
                    textAlign: 'left'
                }
            }}
        >
            <DialogContent sx={{ padding: 3 }}>
                <div style={{ marginBottom: 4 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section1.title" />
                    </Typography>

                    <List dense sx={{ pl: 2, mb: 2 }}>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section1.item1" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section1.item2" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section1.item3" />
                        </ListItem>
                    </List>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section1.notice" />
                    </Typography>

                    <RadioGroup
                        name="consentDataCollection"
                        value={data.consentDataCollection}
                        onChange={(e) => handleOnChangePipaConsent('consentDataCollection', e.target.value as PipaConsentValue)}
                        row
                        sx={{ gap: 2 }}
                    >
                        <FormControlLabel 
                            value="true" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.agree" />} 
                        />
                        <FormControlLabel 
                            value="false" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.disagree" />} 
                        />
                    </RadioGroup>
                </div>

                <div style={{ marginBottom: 4 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section2.title" />
                    </Typography>

                    <List dense sx={{ pl: 2, mb: 2 }}>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section2.item1" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section2.item2" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section2.item3" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section2.item4" />
                        </ListItem>
                    </List>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section2.notice" />
                    </Typography>

                    <RadioGroup
                        name="consentDataThirdparty"
                        value={data.consentDataThirdparty}
                        onChange={(e) => handleOnChangePipaConsent('consentDataThirdparty', e.target.value as PipaConsentValue)}
                        row
                        sx={{ gap: 2 }}
                    >
                        <FormControlLabel 
                            value="true" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.agree" />} 
                        />
                        <FormControlLabel 
                            value="false" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.disagree" />} 
                        />
                    </RadioGroup>
                </div>

                <div>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section3.title" />
                    </Typography>

                    <List dense sx={{ pl: 2, mb: 2 }}>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section3.item1" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section3.item2" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section3.item3" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section3.item4" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, pl: 0, '&::before': { content: '"- "', mr: 1 } }}>
                            <FormattedMessage id="pipa.consent.section3.item5" />
                        </ListItem>
                    </List>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <FormattedMessage id="pipa.consent.section3.notice" />
                    </Typography>

                    <RadioGroup
                        name="consentDataInternational"
                        value={data.consentDataInternational}
                        onChange={(e) => handleOnChangePipaConsent('consentDataInternational', e.target.value as PipaConsentValue)}
                        row
                        sx={{ gap: 2 }}
                    >
                        <FormControlLabel 
                            value="true" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.agree" />} 
                        />
                        <FormControlLabel 
                            value="false" 
                            control={<Radio />} 
                            label={<FormattedMessage id="pipa.consent.disagree" />} 
                        />
                    </RadioGroup>
                </div>
            </DialogContent>

            <DialogActions className='appbar' sx={{ padding: 2, justifyContent: 'center', gap: 2 }}>
                <Button 
                    disableRipple 
                    variant='outlined' 
                    className='cancel-button pipa-consent-modal__button--cancel'
                    onClick={handleOnClickPipaConsentDialogCancel} 
                    sx={{ color: 'black', borderColor: 'black' }}
                >
                    <FormattedMessage id="general.cancel" />
                </Button>
                <Button 
                    disableRipple 
                    variant='outlined' 
                    className='submit-button' 
                    onClick={handleOnClickPipaConsentDialogConfirm} 
                    sx={{ color: 'black', borderColor: 'black' }}
                >
                    <FormattedMessage id="general.confirm" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
